import { useEffect, useState } from 'react';
import { contactsAPI } from '@/lib/api';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, User } from 'lucide-react';

/**
 * Cambiar el solicitante es cambiar **quién puede ver el caso**, no editar un texto.
 *
 * Por eso va por un diálogo de confirmación con motivo y no por el formulario de la ficha: el
 * caso de uso que lo motiva es el reenvío —alguien manda a del.ia un mail que le mandaron, y
 * el pedido es del remitente original—, y ahí se le está dando visibilidad a una persona que
 * hasta ese momento no la tenía.
 *
 * Se **elige** de la lista, no se escribe: un correo tipeado que no corresponde a ninguna
 * persona es una relación que nunca va a resolver, y en pantalla se ve igual que una válida.
 */
export default function CambiarSolicitanteDialog({ open, onOpenChange, actual, onConfirm }) {
  const [query, setQuery] = useState('');
  const [personas, setPersonas] = useState([]);
  const [elegida, setElegida] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setElegida(null);
    setMotivo('');
    buscar('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const buscar = async (q) => {
    setLoading(true);
    try {
      const res = await contactsAPI.list({ q: q || undefined, limit: 20 });
      setPersonas(Array.isArray(res.data) ? res.data : (res.data?.items || []));
    } catch {
      setPersonas([]);
    } finally {
      setLoading(false);
    }
  };

  const correoDe = (p) =>
    (p.identities || []).find((i) => i.channel_type === 'email')?.external_id_normalized || null;

  return (
    <ConfirmActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Cambiar solicitante"
      description="Quien figure como solicitante va a poder ver este caso en 'Mis solicitudes', y su jefe tambien. Queda registrado quien lo cambio."
      confirmLabel="Cambiar solicitante"
      confirmDisabled={!elegida}
      onConfirm={() => onConfirm(elegida.contact_id, motivo)}
    >
      <div className="space-y-3">
        <div className="text-xs text-zinc-600">
          Ahora: <span className="font-medium">{actual?.nombre || '—'}</span>
          {actual?.email ? <span className="text-zinc-400"> · {actual.email}</span> : null}
        </div>

        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-400" />
          <Input
            autoFocus
            className="pl-7 h-8 text-xs rounded-sm"
            placeholder="Buscar persona por nombre..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); buscar(e.target.value); }}
          />
        </div>

        <div className="border border-brand-border rounded-sm max-h-48 overflow-auto">
          {loading && <div className="p-2 text-[10px] text-zinc-400">Buscando...</div>}
          {!loading && personas.length === 0 && (
            <div className="p-2 text-[10px] text-zinc-500">
              Sin coincidencias. El solicitante tiene que ser una persona que ya exista.
            </div>
          )}
          {personas.map((p) => {
            const correo = correoDe(p);
            const activa = elegida?.contact_id === p.contact_id;
            return (
              <button
                key={p.contact_id}
                type="button"
                onClick={() => setElegida(p)}
                className={`w-full text-left px-3 py-2 text-xs border-b border-zinc-50 last:border-0 ${
                  activa ? 'bg-brand-primary/10' : 'hover:bg-zinc-50'
                }`}
              >
                <span className="font-medium flex items-center gap-1">
                  <User className="w-3 h-3 text-zinc-400" />
                  {p.display_name || p.contact_id}
                </span>
                <span className="text-[10px] text-zinc-500 ml-4">
                  {correo || 'sin correo — no va a poder ver el caso'}
                </span>
              </button>
            );
          })}
        </div>

        <div>
          <Label className="text-[10px] text-zinc-500">Motivo (opcional)</Label>
          <Input
            className="h-8 text-xs mt-0.5 rounded-sm"
            placeholder="Reenvio: el pedido es del remitente original"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      </div>
    </ConfirmActionDialog>
  );
}
